import { useEffect, useMemo, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import { UserAccessOptions } from "../../../Enums";
import { normalizeRoleToKey } from "../../../Enums/UserEnums";
import {
  formatWsgStaffRoleLabel,
  isWsgStaffProfilesFirm,
} from "../../../pages/ManageFirm/wsgStaffProfiles";
import { useAuth } from "../../../store/AuthContext";
import DropDownMultiple from "../../staff/DropDownMultiple";
import InputText from "../../staff/InputText";
import ToggleSwitch from "../../staff/ToogleSwitch";
import { usePersonForm } from "./personForm/PersonFormContext";
import {
  PERSON_ROLE_MEMBER,
  PERSON_ROLE_WSG_STAFF,
  getProfessionalScreenRoleKeys,
  getWsgStaffScreenRoleKeys,
  isPersonRoleDataAdmin,
} from "./personForm/personDataAdminUtils";

function buildSuggestedUsername(firstName: string, lastName: string): string {
  const f = firstName.trim();
  const l = lastName.trim().replace(/\s+/g, "");
  if (!f || !l) {
    return "";
  }
  return `${f.charAt(0).toUpperCase()}${l}`;
}

const User = () => {
  const {
    state,
    form,
    mode,
    setField,
    touchField,
    getFieldError,
    shouldShowError,
  } = usePersonForm();
  const { loggedPerson, tokenDecoded } = useAuth();

  const { pathname } = useLocation();
  const { id: routeFirmId } = useParams<{ id?: string }>();
  const actorRole =
    tokenDecoded?.extension_WSG_Permissions ?? loggedPerson?.role;
  const actorIsDataAdmin = isPersonRoleDataAdmin(actorRole);
  const staffProfilesScreen = isWsgStaffProfilesFirm(form.firm);
  const showWsgMemberToggle =
    isWsgStaffProfilesFirm(routeFirmId) &&
    /\/professionals\/[^/]+\/(create|edit)(\/|$)/i.test(pathname);

  const allowedRoleIds = new Set(
    staffProfilesScreen
      ? getWsgStaffScreenRoleKeys(actorRole)
      : getProfessionalScreenRoleKeys(
          actorRole,
          mode === "edit" ? form.role : undefined,
        ),
  );

  const roleDropdownOptions = UserAccessOptions.filter((option) =>
    allowedRoleIds.has(String(option.id ?? "")),
  ).map((option) => ({
    id: String(option.id ?? ""),
    name: staffProfilesScreen
      ? formatWsgStaffRoleLabel(String(option.id ?? "")) ||
        String(option.name ?? "")
      : String(option.name ?? ""),
  }));

  useEffect(() => {
    if (staffProfilesScreen) {
      const allowed = getWsgStaffScreenRoleKeys(actorRole);
      const currentRole = normalizeRoleToKey(form.role);
      if (!currentRole || !allowed.includes(currentRole)) {
        setField("role", allowed[0] ?? PERSON_ROLE_WSG_STAFF);
      }
      return;
    }
    if (!actorIsDataAdmin) {
      return;
    }
    if (mode === "edit" && normalizeRoleToKey(form.role) === "dataAdmin") {
      return;
    }
    const currentRole = normalizeRoleToKey(form.role);
    if (currentRole !== PERSON_ROLE_MEMBER) {
      setField("role", PERSON_ROLE_MEMBER);
    }
  }, [
    actorIsDataAdmin,
    actorRole,
    mode,
    form.role,
    setField,
    staffProfilesScreen,
  ]);

  const suggestedUsername = useMemo(
    () => buildSuggestedUsername(form.name ?? "", form.lastName ?? ""),
    [form.name, form.lastName]
  );

  const lastAutoUsernameRef = useRef<string>("");

  useEffect(() => {
    if (state.touched.username) {
      return;
    }

    if (!suggestedUsername) {
      lastAutoUsernameRef.current = "";
      return;
    }

    const current = form.username?.trim() ?? "";
    const matchesPriorSuggestion =
      current === "" || current === lastAutoUsernameRef.current;

    if (!matchesPriorSuggestion) {
      return;
    }

    if (form.username !== suggestedUsername) {
      setField("username", suggestedUsername);
    }
    lastAutoUsernameRef.current = suggestedUsername;
  }, [
    suggestedUsername,
    form.username,
    form.name,
    form.lastName,
    state.touched.username,
    setField,
  ]);

  const usernamePlaceholder =
    suggestedUsername &&
    !form.username?.trim() &&
    !state.touched.username
      ? suggestedUsername
      : undefined;

  const selectedRoleId = normalizeRoleToKey(form.role);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
      <DropDownMultiple
        title="Role"
        required
        searchable={false}
        options={roleDropdownOptions}
        value={roleDropdownOptions.find((o) => o.id === selectedRoleId) ?? null}
        onChange={(opt) => {
          setField("role", opt?.id ?? "");
          touchField("role");
        }}
        error={shouldShowError("role") && Boolean(getFieldError("role"))}
        errorMessage={getFieldError("role")}
      />
      <InputText
        title="Username"
        required
        value={form.username ?? ""}
        placeholder={usernamePlaceholder}
        onChange={(v) => setField("username", v)}
        onBlur={() => touchField("username")}
        error={shouldShowError("username") && Boolean(getFieldError("username"))}
        errorMessage={getFieldError("username")}
      />
      {showWsgMemberToggle ? (
        <ToggleSwitch
          labelTop
          label="Is WSG Member"
          checked={form.isWsgMember === true}
          onChange={(checked) => setField("isWsgMember", checked)}
        />
      ) : null}
    </div>
  );
};

export default User;
